// A popular route is a town with a flat fare the admin typed. A rider who picks a place INSIDE
// that town (the station, a hospital, a hotel) is making the same trip, so it costs the same flat
// fare, and the rider keeps their own spot as the drop. Anywhere else is a distance trip, priced
// per km as before. These are the rules for that, free of React and Firebase so they can be
// tested on their own. The server still prices whatever the app asks for; this only decides
// which of the two trips the app asks for.

import { routeFare } from './intertownDisplay';
import { isTimeoutError, withTimeout } from './intertownWait';

// How a route's town radius is read. Same default and clamp as the server's normaliseConfig
// (firebase/functions/intertown.js): it is the radius the server uses for a fixed route's
// arrival, and the admin panel saves 8 on every route. A guard test compares the two.
export const DEFAULT_TOWN_RADIUS_KM = 8;
export const townRadiusKm = (route) => {
  const km = Number(route?.destinationRadiusKm);
  return Number.isFinite(km) ? Math.min(25, Math.max(1, km)) : DEFAULT_TOWN_RADIUS_KM;
};

// Finding the town's centre on the map is a billed lookup, remembered for 30 days per device
// (intertownTown.js). Matching a drop must never hold the rider up for it.
export const DROP_MATCH_TIMEOUT_MS = 4000;

const num = (value) => (value === null || value === undefined || value === '' ? NaN : Number(value));
const isPoint = (place) => Number.isFinite(num(place?.lat)) && Number.isFinite(num(place?.lng));
const own = (table, key) => (table && Object.prototype.hasOwnProperty.call(table, key) ? table[key] : undefined);

/** Straight-line distance between two places in km (the same measure the server uses). */
export function distanceKm(a, b) {
  const rad = (degrees) => (degrees * Math.PI) / 180;
  const dLat = rad(num(b.lat) - num(a.lat));
  const dLng = rad(num(b.lng) - num(a.lng));
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(rad(num(a.lat))) * Math.cos(rad(num(b.lat))) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
}

/**
 * The town centre the admin gave a route (centerLat / centerLng), or null when there is none. The
 * server measures the town radius from the same point and charges the flat fare to a spot inside it
 * itself, so when it is set the app uses it too: the two can never disagree about where the town is,
 * and no billed map lookup is needed. The India-only box the server holds every place to
 * (firebase/functions/intertown.js, townCentreOf).
 */
export const routeCentre = (route) => {
  const lat = num(route?.centerLat);
  const lng = num(route?.centerLng);
  return Number.isFinite(lat) && Number.isFinite(lng) && lat >= 5 && lat <= 38 && lng >= 68 && lng <= 98 ? { lat, lng } : null;
};

/** The route's town as a place (name, address, point) from the admin's centre, or null when it has none. */
export const townPlace = (route) => {
  const centre = routeCentre(route);
  const name = String(route?.toName || '').trim();
  return centre && name ? { name, address: `${name}, Andhra Pradesh, India`, ...centre } : null;
};

/**
 * Has the admin typed a flat fare for this trip type? Exactly the server's own test for a
 * quote's pricingMode === 'fixed' (a blank fare means the route is priced per km, and then a
 * place in that town is better priced by its real distance than by the route's nominal km).
 */
export const hasFlatFare = (route, tripType) => routeFare(route, tripType, 0).mode === 'fixed';

/** The routes a drop could belong to for this trip type: on, with a flat fare. */
export const flatFareRoutes = (routes, tripType) => (
  (Array.isArray(routes) ? routes : []).filter((route) => route && route.enabled !== false && hasFlatFare(route, tripType))
);

/**
 * The popular route a place belongs to: the nearest town centre within that route's town
 * radius, among the routes with a flat fare for this trip type. `towns` maps a route id to the
 * town's resolved centre; a route whose centre is unknown is simply skipped. Null means the place
 * is in none of them, so it is a distance trip.
 */
export function popularRouteFor({ place, routes, towns, tripType }) {
  if (!isPoint(place)) return null;
  let best = null;
  for (const route of flatFareRoutes(routes, tripType)) {
    const centre = own(towns, route.id);
    if (!isPoint(centre)) continue;
    const km = distanceKm(place, centre);
    if (km <= townRadiusKm(route) && (best === null || km < best.km)) best = { route, km };
  }
  return best ? best.route : null;
}

/**
 * Looks up the centre of every route that could match, all at once, each with its own time limit.
 * `lookup(route)` is the page's remembered town lookup. A route that fails or times out is left
 * out (its drops stay distance trips, which is always a safe answer); `onTimeout(route)` lets
 * the page drop a lookup that never answered so the next try starts fresh.
 */
export async function resolvePopularTowns(routes, tripType, { lookup, timeoutMs = DROP_MATCH_TIMEOUT_MS, onTimeout } = {}) {
  const entries = await Promise.all(flatFareRoutes(routes, tripType).map(async (route) => {
    // The admin's own centre needs no lookup at all.
    const fixed = routeCentre(route);
    if (fixed) return [route.id, fixed];
    try {
      const place = await withTimeout(Promise.resolve(lookup(route)), timeoutMs, 'town');
      return isPoint(place) ? [route.id, { lat: num(place.lat), lng: num(place.lng) }] : null;
    } catch (error) {
      if (isTimeoutError(error)) onTimeout?.(route);
      return null;
    }
  }));
  return Object.fromEntries(entries.filter(Boolean));
}
